/*
 * Um seletor com busca, no tamanho do que o composer precisa: um
 * cabeçalho que abre, logo embaixo (ou em cima, quando falta espaço), o
 * corpo com o campo "Pesquisar…" e a lista. Setas sobem e descem, Enter
 * escolhe, Esc fecha; clicar fora fecha.
 *
 * Quem usa passa as linhas já filtradas (`rows(filter)`), o conteúdo do
 * cabeçalho e o que fazer ao escolher — o componente só cuida de abrir,
 * posicionar e navegar.
 */
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';
import classList from 'flarum/common/utils/classList';
import { t } from '../utils/settings';
const BODY_MIN_SPACE = 300;
export default class SelectKit extends Component {
  open = false;
  filter = '';
  highlighted = 0;
  above = false;
  outside = null;
  view() {
    const {
      className,
      header,
      caret = 'fas fa-angle-down',
      caretOpen = 'fas fa-angle-up',
      disabled,
      label
    } = this.attrs;
    return m("div", {
      className: classList('CanelaSelectKit', className, {
        'is-expanded': this.open,
        'is-above': this.above,
        'is-disabled': disabled
      })
    }, m("button", {
      type: "button",
      className: "CanelaSelectKit-header",
      disabled: disabled,
      "aria-haspopup": "listbox",
      "aria-expanded": this.open ? 'true' : 'false',
      "aria-label": label,
      onclick: () => this.open ? this.close() : this.show(),
      onkeydown: e => {
        if (!this.open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          this.show();
        }
      }
    }, m("span", {
      className: "CanelaSelectKit-headerContent"
    }, header), m(Icon, {
      name: this.open ? caretOpen : caret,
      className: "CanelaSelectKit-caret"
    })), this.open && this.bodyView());
  }
  bodyView() {
    const rows = this.attrs.rows(this.filter);
    if (this.highlighted >= rows.length) this.highlighted = rows.length - 1;
    const filter = m("div", {
      className: "CanelaSelectKit-filter"
    }, m("input", {
      type: "search",
      className: "CanelaSelectKit-filterInput",
      placeholder: this.attrs.placeholder || t('composer.shell.search_placeholder'),
      value: this.filter,
      autocomplete: "off",
      spellcheck: false,
      oninput: e => {
        this.filter = e.target.value;
        this.highlighted = 0;
      },
      onkeydown: e => this.onkeydown(e, rows),
      oncreate: vnode => vnode.dom.focus()
    }), this.attrs.filterIcon && m(Icon, {
      name: "fas fa-magnifying-glass",
      className: "CanelaSelectKit-filterIcon"
    }));
    const list = m("ul", {
      className: "CanelaSelectKit-collection",
      role: "listbox",
      "aria-label": this.attrs.label
    }, rows.length ? rows.map((row, i) => m("li", {
      key: row.key,
      className: classList('CanelaSelectKit-row', {
        'is-highlighted': i === this.highlighted,
        'is-selected': row.selected
      }),
      role: "option",
      "aria-selected": row.selected ? 'true' : 'false',
      title: row.title,
      onmouseenter: () => this.highlighted = i
      // `mousedown` e não `click`: escolher antes que o campo perca o foco.
      ,
      onmousedown: e => {
        e.preventDefault();
        this.choose(row.key);
      }
    }, row.content)) : m("li", {
      key: "empty",
      className: "CanelaSelectKit-empty"
    }, this.attrs.emptyText || t('composer.shell.no_results')));
    const after = typeof this.attrs.after === 'function' ? this.attrs.after(this.filter) : this.attrs.after;

    // Aberto para cima, a busca fica embaixo, perto do cabeçalho.
    return m("div", {
      className: "CanelaSelectKit-body",
      onmousedown: e => e.stopPropagation()
    }, this.above ? [after, list, this.attrs.before, filter] : [filter, this.attrs.before, list, after]);
  }
  onkeydown(e, rows) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlighted = Math.min(rows.length - 1, this.highlighted + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlighted = Math.max(0, this.highlighted - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[this.highlighted];
      if (row) this.choose(row.key);
    } else if (e.key === 'Escape') {
      // Não deixa o Esc chegar ao composer (que fecharia tudo).
      e.preventDefault();
      e.stopPropagation();
      this.close();
      this.element.querySelector('.CanelaSelectKit-header')?.focus();
    } else if (e.key === 'Tab') {
      this.close();
    }
    this.scrollHighlightedIntoView();
  }
  scrollHighlightedIntoView() {
    requestAnimationFrame(() => {
      this.element?.querySelector('.CanelaSelectKit-row.is-highlighted')?.scrollIntoView({
        block: 'nearest'
      });
    });
  }
  choose(key) {
    this.attrs.onselect(key);
    this.filter = '';
    if (!this.attrs.stayOpen) this.close();
    m.redraw();
  }
  show() {
    if (this.attrs.disabled) return;

    // Abre para cima quando falta espaço embaixo e sobra em cima.
    const rect = this.element.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    this.above = below < BODY_MIN_SPACE && rect.top > below;
    this.open = true;
    this.filter = '';
    this.highlighted = this.attrs.highlightSelected ? this.attrs.rows('').findIndex(row => row.selected) : 0;
    this.outside = e => {
      if (!this.element.contains(e.target)) {
        this.close();
        m.redraw();
      }
    };
    document.addEventListener('mousedown', this.outside);
  }
  close() {
    this.open = false;
    if (this.outside) document.removeEventListener('mousedown', this.outside);
    this.outside = null;
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.close();
  }
}
flarum.reg.add('ramon-canela', 'forum/composer/SelectKit', SelectKit);